<template>
  <div class="interview-container">
    <!-- 页面标题 -->
    <div class="page-header">
      <h1 class="page-title">AI军招面试</h1>
      <p class="page-subtitle">AI军招面试智能助手</p>
    </div>

    <!-- 聊天区域 -->
    <div class="chat-container">
      <!-- 消息列表 -->
      <div class="message-list" ref="messageList">
        <!-- 系统欢迎消息 -->
        <div v-if="currentQuestionIndex === 0 && !showResult" class="system-message">
          <div class="system-icon">🎖️</div>
          <div class="message-content">
            <p>欢迎使用AI军招面试模拟系统！系统将围绕军人八大核心能力，向您提出10道面试题，评估您的政治素养、综合素养、心理素质、表达能力、协作能力、应变能力、纪律意识、潜力与发展等方面，请您真实作答，助力您更好应对军校面试，圆梦军营。</p>
          </div>
          <div class="message-time">{{ getCurrentTime() }}</div>
        </div>

        <!-- 已完成的问题和回答 -->
        <div 
          v-for="(item, index) in conversation" 
          :key="index"
          class="conversation-item"
        >
          <!-- AI问题 -->
          <div class="message-row ai-row">
            <div class="avatar-wrapper">
              <div class="avatar ai-avatar">
                <i class="fa fa-robot"></i>
              </div>
            </div>
            <div class="message-content-wrapper">
              <div class="message-bubble ai-bubble">
                <div class="question-meta">
                  <span class="question-badge">第 {{ item.questionNumber }} 题</span>
                  <span class="message-time">{{ item.time }}</span>
                </div>
                <p class="message-text">{{ item.question }}</p>
              </div>
            </div>
          </div>

          <!-- 用户回答 -->
          <div class="message-row user-row">
            <div class="message-content-wrapper">
              <div class="message-bubble user-bubble">
                <div class="question-meta user-meta">
                  <span class="message-time">{{ item.answerTime }}</span>
                </div>
                <p class="message-text">{{ item.answer }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- 当前问题 -->
        <div v-if="!showResult && currentQuestionIndex < questions.length" class="current-question">
          <div class="message-row ai-row">
            <div class="avatar-wrapper">
              <div class="avatar ai-avatar">
                <i class="fa fa-robot"></i>
              </div>
            </div>
            <div class="message-content-wrapper">
              <div class="message-bubble ai-bubble">
                <div class="question-meta">
                  <span class="question-badge">第 {{ currentQuestionIndex + 1 }} 题</span>
                  <span class="message-time">{{ getCurrentTime() }}</span>
                </div>
                <p class="message-text">{{ questions[currentQuestionIndex].title }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- 完成提示 -->
        <div v-if="!showResult && currentQuestionIndex === questions.length && conversation.length > 0" class="system-message">
          <div class="system-icon">🎉</div>
          <div class="message-content">
            <p>已完成全部题目！点击下方按钮查看AI评判结果</p>
          </div>
          <div class="message-time">{{ getCurrentTime() }}</div>
        </div>
        
        <!-- AI评判按钮 -->
        <div v-if="currentQuestionIndex === questions.length && conversation.length > 0 && !showResult" class="evaluate-section">
          <el-button type="primary" class="evaluate-btn" @click="handleEvaluate" :loading="isLoading">
            <i class="fa fa-check-circle"></i> AI智能评判
          </el-button>
        </div>

        <!-- AI评判完成提示 -->
        <div v-if="showResult" class="system-message success">
          <div class="system-icon">✅</div>
          <div class="message-content">
            <p>AI评判完成！请查看评判结果。</p>
          </div>
          <div class="message-time">{{ getCurrentTime() }}</div>
        </div>
      </div>

      <!-- 输入区域 -->
      <div v-if="!showResult" class="input-area">
        <div class="input-wrapper">
          <textarea 
            ref="answerInput"
            v-model="currentAnswer"
            placeholder="请输入您的回答..."
            class="answer-input"
            :disabled="currentQuestionIndex >= questions.length"
            @keyup.enter.exact="submitAnswer"
          ></textarea>
          <div class="input-actions">
            <div class="input-hints">
              <span class="char-count">{{ currentAnswer.length }}/500</span>
              <span v-if="!currentAnswer.trim() && currentQuestionIndex < questions.length" class="hint-text">按 Enter 快速提交</span>
            </div>
            <el-button 
              type="primary" 
              @click="submitAnswer"
              :disabled="currentQuestionIndex < questions.length && !currentAnswer.trim()"
              class="submit-btn"
            >
              <i class="fa fa-paper-plane"></i>
              提交答案
            </el-button>
          </div>
        </div>
      </div>
    </div>

    <!-- 进度条 -->
    <div v-if="!showResult" class="progress-container">
      <div class="progress-info">
        <span class="progress-text">答题进度</span>
        <span class="progress-count">{{ conversation.length }}/{{ questions.length }}</span>
      </div>
      <div class="progress-bar">
        <div class="progress-fill" :style="{ width: progressPercent + '%' }"></div>
        <div class="progress-glow" :style="{ left: progressPercent + '%' }"></div>
      </div>
    </div>

    <!-- 评判结果弹窗 -->
    <div v-if="showResult" class="result-overlay" @click.self="closeResult">
      <div class="result-modal">
        <div class="result-header" :class="evaluation.grade.toLowerCase()">
          <div class="result-icon">
            <i v-if="evaluation.grade === '优秀'" class="fa fa-trophy"></i>
            <i v-else-if="evaluation.grade === '合格'" class="fa fa-check-circle"></i>
            <i v-else class="fa fa-times-circle"></i>
          </div>
          <h2 class="result-title">面试评判结果</h2>
          <div class="score-display">
            <span class="score">{{ evaluation.score }}</span>
            <span class="score-unit">分</span>
          </div>
        </div>
        
        <div class="result-body">
          <div class="result-item grade-item">
            <span class="label">评定等级：</span>
            <span class="value grade" :class="evaluation.grade.toLowerCase()">{{ evaluation.grade }}</span>
          </div>
          
          <div class="result-section">
            <h3>
              <i class="fa fa-file-text"></i>
              总体评价
            </h3>
            <p>{{ evaluation.overallComment }}</p>
          </div>
          
          <div class="result-section">
            <h3>
              <i class="fa fa-lightbulb-o"></i>
              改进建议
            </h3>
            <ul>
              <li v-for="(suggestion, index) in evaluation.suggestions" :key="index">
                <span class="suggestion-number">{{ index + 1 }}.</span>
                {{ suggestion }}
              </li>
            </ul>
          </div>
          
          <!-- 每道题详细评判 -->
          <div v-if="evaluation.details.length > 0" class="result-section">
            <h3>
              <i class="fa fa-list-alt"></i>
              答题详情
            </h3>
            <div class="details-list">
              <div 
                v-for="(detail, index) in evaluation.details" 
                :key="index" 
                class="detail-item"
              >
                <div class="detail-header">
                  <span class="detail-number">第 {{ index + 1 }} 题</span>
                  <span class="detail-score" :class="getScoreClass(detail.score)">
                    {{ detail.score }}分
                  </span>
                </div>
                <div class="detail-question">
                  <span class="label">题目：</span>
                  {{ detail.question }}
                </div>
                <div class="detail-answer">
                  <span class="label">你的回答：</span>
                  {{ detail.userAnswer }}
                </div>
                <div class="detail-strengths" v-if="detail.strengths && detail.strengths.length > 0">
                  <span class="label strengths-label">优点：</span>
                  <div class="tag-list strengths">
                    <span v-for="(strength, sIndex) in detail.strengths" :key="sIndex" class="tag">
                      {{ strength }}
                    </span>
                  </div>
                </div>
                <div class="detail-weaknesses" v-if="detail.weaknesses && detail.weaknesses.length > 0">
                  <span class="label weaknesses-label">不足：</span>
                  <div class="tag-list weaknesses">
                    <span v-for="(weakness, wIndex) in detail.weaknesses" :key="wIndex" class="tag">
                      {{ weakness }}
                    </span>
                  </div>
                </div>
                <div class="detail-improvement" v-if="detail.improvement">
                  <span class="label improvement-label">改进建议：</span>
                  <span class="improvement-text">{{ detail.improvement }}</span>
                </div>
              </div>
            </div>
          </div>
          
          <!-- AI生成内容免责声明 -->
          <div class="disclaimer-section">
            <div class="disclaimer-content">
              <i class="fa fa-info-circle"></i>
              <span>本内容由 AI 生成，仅供参考，具体请以官方最新政策为准</span>
            </div>
          </div>
        </div>
        
        <div class="result-footer">
          <el-button type="default" @click="closeResult" class="close-btn">
            <i class="fa fa-times"></i> 关闭
          </el-button>
          <el-button type="primary" @click="resetInterview" class="retest-btn">
            <i class="fa fa-refresh"></i> 重新答题
          </el-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, watch, onMounted } from 'vue';
import { getRandomInterview, getInterviewAnswers, evaluateInterview, saveInterview } from "@/api/processApi";

const currentQuestionIndex = ref(0);
const currentAnswer = ref('');
const showResult = ref(false);
const messageList = ref(null);
const isLoading = ref(false);
const answerInput = ref(null);

// 从接口获取的问题列表
const questions = ref([]);
// 默认问题列表作为备选
const defaultQuestions = [
  { id: 1, numb: 1, title: '新时代对革命军人的要求是什么？', type: '综合素养' },
  { id: 2, numb: 2, title: '你如何看待军人在社会中的角色？', type: '价值观' },
  { id: 3, numb: 3, title: '你最大的优点是什么？', type: '自我认知' },
  { id: 4, numb: 4, title: '你对边海防有什么感悟？', type: '价值观' },
  { id: 5, numb: 5, title: '你认为军人和普通人的区别？', type: '综合素养' },
  { id: 6, numb: 6, title: '考试考核失利，你会怎么做？', type: '心理素质' },
  { id: 7, numb: 7, title: '你觉得军校培养你的核心是什么？', type: '动机类' },
  { id: 8, numb: 8, title: '什么是强军梦？', type: '政治素养' },
  { id: 9, numb: 9, title: '你为报考军校做了哪些准备？', type: '动机类' },
  { id: 10, numb: 10, title: '你在团队中如何发挥作用？', type: '协作能力' }
];

const conversation = ref([]);

const progressPercent = computed(() => {
  return (conversation.value.length / questions.value.length) * 100;
});

// AI评价结果
const evaluation = ref({
  score: 0,
  grade: '待评价',
  overallComment: '',
  suggestions: [],
  details: []
});

const getCurrentTime = () => {
  const now = new Date();
  return `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
};

// 根据分数返回样式类
const getScoreClass = (score) => {
  if (score >= 8) return 'score-high';
  if (score >= 6) return 'score-medium';
  if (score >= 4) return 'score-low';
  return 'score-fail';
};

// 获取面试题
const fetchQuestions = () => {
  getRandomInterview().then(response => {
    if (response.data.code === 200) {
      questions.value = response.data.data;
      // 如果接口返回的题目数量不足，补充默认题目
      if (questions.value.length < 10) {
        const existingIds = new Set(questions.value.map(q => q.id));
        const additional = defaultQuestions.filter(q => !existingIds.has(q.id));
        questions.value = [...questions.value, ...additional].slice(0, 10);
      }
    } else {
      // 接口失败，使用默认题目
      questions.value = [...defaultQuestions];
    }
  }).catch(error => {
    console.error('获取面试题失败:', error);
    questions.value = [...defaultQuestions];
  });
};

// 提交答案
const submitAnswer = () => {
  if (!currentAnswer.value.trim()) return;
  
  if (currentQuestionIndex.value < questions.value.length) {
    conversation.value.push({
      questionNumber: currentQuestionIndex.value + 1,
      questionId: questions.value[currentQuestionIndex.value].id,
      question: questions.value[currentQuestionIndex.value].title,
      answer: currentAnswer.value.trim(),
      time: getCurrentTime(),
      answerTime: getCurrentTime()
    });
    
    currentAnswer.value = '';
    currentQuestionIndex.value++;
    
    nextTick(() => {
      if (messageList.value) {
        messageList.value.scrollTop = messageList.value.scrollHeight;
      }
      // 让输入框保持焦点
      if (answerInput.value) {
        answerInput.value.focus();
      }
    });
  }
};

// 解析可能被双重编码的JSON
const parseEvaluation = (evaluationStr) => {
  if (!evaluationStr) {
    return null;
  }
  
  console.log('原始evaluation字符串:', evaluationStr);
  
  let str = evaluationStr.toString().trim();
  
  // 尝试多种解析策略
  const strategies = [
    // 策略1: 直接解析
    () => JSON.parse(str),
    // 策略2: 去除首尾引号后解析
    () => {
      if (str.startsWith('"') && str.endsWith('"')) {
        const inner = str.slice(1, -1);
        return JSON.parse(inner.replace(/\\"/g, '"').replace(/\\\\/g, '\\'));
      }
      throw new Error('Not quoted');
    },
    // 策略3: 处理可能的HTML实体编码
    () => {
      const decoded = str.replace(/&quot;/g, '"').replace(/&amp;/g, '&');
      return JSON.parse(decoded);
    },
    // 策略4: 查找第一个完整JSON对象
    () => {
      const firstBrace = str.indexOf('{');
      const lastBrace = str.lastIndexOf('}');
      if (firstBrace !== -1 && lastBrace !== -1 && firstBrace < lastBrace) {
        const jsonStr = str.substring(firstBrace, lastBrace + 1);
        return JSON.parse(jsonStr);
      }
      throw new Error('No JSON object found');
    },
    // 策略5: 使用eval（最后手段）
    () => {
      // eslint-disable-next-line no-eval
      return eval('(' + str + ')');
    }
  ];
  
  for (let i = 0; i < strategies.length; i++) {
    try {
      const result = strategies[i]();
      console.log(`解析成功，使用策略${i + 1}`, result);
      return result;
    } catch (e) {
      console.log(`策略${i + 1}解析失败:`, e.message);
    }
  }
  
  console.error('所有解析策略均失败');
  return null;
};

// AI评判
const handleEvaluate = async () => {
  if (conversation.value.length === 0) return;
  
  isLoading.value = true;
  
  try {
    // 获取问题ID列表
    const questionIds = conversation.value.map(item => item.questionId);
    
    // 先获取正确答案
    const answerResponse = await getInterviewAnswers(questionIds);
    const answerMap = {};
    if (answerResponse.data.code === 200) {
      const answers = answerResponse.data.data;
      answers.forEach(answer => {
        answerMap[answer.questionId] = answer.correctAnswer || '';
      });
    }
    
    // 构建问答列表（包含正确答案）
    const qaList = conversation.value.map(item => ({
      questionId: item.questionId,
      question: item.question,
      userAnswer: item.answer,
      correctAnswer: answerMap[item.questionId] || ''
    }));
    
    // AI评判
    const evalResponse = await evaluateInterview(qaList);
    if (evalResponse.data.code === 200) {
      const data = evalResponse.data.data;
      
      console.log('=== 调试信息 ===');
      console.log('data:', data);
      
      // 新接口返回的evaluation是直接的JSON对象，不需要解析字符串
      const evalResult = data.evaluation;
      
      console.log('evaluation结果:', evalResult);
      console.log('evaluation类型:', typeof evalResult);
      
      if (evalResult && typeof evalResult === 'object') {
        // 提取suggestions
        let suggestions = [];
        if (evalResult.suggestions && Array.isArray(evalResult.suggestions)) {
          suggestions = evalResult.suggestions;
        } else if (evalResult.suggestion && Array.isArray(evalResult.suggestion)) {
          suggestions = evalResult.suggestion;
        }
        
        // 如果suggestions仍为空，尝试从details中提取建议
        if (suggestions.length === 0 && evalResult.details && Array.isArray(evalResult.details)) {
          suggestions = evalResult.details.map(d => {
            if (d.improvement) return d.improvement;
            if (d.suggestion) return d.suggestion;
            return null;
          }).filter(Boolean);
        }
        
        evaluation.value = {
          score: evalResult.score !== undefined ? evalResult.score : (evalResult.totalScore !== undefined ? evalResult.totalScore : 0),
          grade: evalResult.grade || evalResult.level || '待评价',
          overallComment: evalResult.overallComment || evalResult.comment || evalResult.evaluation || '',
          suggestions: suggestions,
          details: evalResult.details && Array.isArray(evalResult.details) ? evalResult.details : []
        };
        console.log('最终evaluation:', evaluation.value);
      } else {
        console.error('评价结果解析失败');
        // 使用兜底方案：当evaluation不是有效对象时，使用rawText或模拟数据
        if (data.rawText) {
          evaluation.value = {
            score: 0,
            grade: '待评价',
            overallComment: data.rawText.length > 500 ? data.rawText.substring(0, 500) + '...' : data.rawText,
            suggestions: []
          };
        } else {
          throw new Error('无效的评价结果');
        }
      }
      
      // 保存面试记录
      saveInterview({
        evaluationResult: evaluation.value,
        qaList: qaList
      }).catch(error => {
        console.error('保存面试记录失败:', error);
      });
      
      showResult.value = true;
    } else {
      console.error('AI评判失败:', evalResponse.data.msg);
    }
  } catch (error) {
    console.error('AI评判失败:', error);
    // 使用模拟评价结果
    evaluation.value = {
      score: 85,
      grade: '优秀',
      overallComment: '回答较好，展现出良好的政治素养和综合能力。',
      suggestions: [
        '继续加强理论学习，提高政治理论水平。',
        '注重语言表达的逻辑性和条理性。',
        '多参与实践活动，提升综合素质。'
      ]
    };
    showResult.value = true;
  } finally {
    isLoading.value = false;
  }
};

const resetInterview = () => {
  currentQuestionIndex.value = 0;
  currentAnswer.value = '';
  conversation.value = [];
  showResult.value = false;
  evaluation.value = {
    score: 0,
    grade: '待评价',
    overallComment: '',
    suggestions: []
  };
  
  // 重新获取题目
  fetchQuestions();
  
  nextTick(() => {
    if (messageList.value) {
      messageList.value.scrollTop = 0;
    }
  });
};

const closeResult = () => {
  showResult.value = false;
  currentQuestionIndex.value = 0;
  currentAnswer.value = '';
  conversation.value = [];
  evaluation.value = {
    score: 0,
    grade: '待评价',
    overallComment: '',
    suggestions: []
  };
  
  fetchQuestions();
  
  nextTick(() => {
    if (messageList.value) {
      messageList.value.scrollTop = 0;
    }
  });
};

onMounted(() => {
  fetchQuestions();
});

watch(conversation, () => {
  nextTick(() => {
    if (messageList.value) {
      messageList.value.scrollTop = messageList.value.scrollHeight;
    }
  });
});
</script>

<style scoped>
.interview-container {
  min-height: 100vh;
  margin-top: 70px;
  padding: 10px;
  background: linear-gradient(180deg, #eff6ff 0%, #dbeafe 50%, #eff6ff 100%);
}

.page-header {
  text-align: center;
  margin-bottom: 15px;
}

.page-title {
  font-size: 24px;
  color: #1e40af;
  margin: 0 0 6px 0;
  font-weight: 600;
}

.page-subtitle {
  font-size: 14px;
  color: #64748b;
  margin: 0;
  letter-spacing: 1px;
}

.chat-container {
  max-width: 100%;
  margin: 0 auto;
  background: #ffffff;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  height: calc(100vh - 280px);
  display: flex;
  flex-direction: column;
  border: 1px solid #e2e8f0;
}

.message-list {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
}

.message-list::-webkit-scrollbar {
  width: 4px;
}

.message-list::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 2px;
}

.message-list::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 2px;
}

.message-list::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.system-message {
  text-align: center;
  margin-bottom: 15px;
  animation: fadeIn 0.3s ease;
}

.system-icon {
  font-size: 28px;
  margin-bottom: 8px;
}

.system-message .message-content {
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border: 1px solid #93c5fd;
  border-radius: 10px;
  padding: 12px 18px;
  display: inline-block;
  max-width: 90%;
  position: relative;
}

.system-message .message-content::before {
  content: '';
  position: absolute;
  top: -6px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-bottom: 6px solid #93c5fd;
}

.system-message.success .message-content {
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border-color: #86efac;
}

.system-message.success .message-content::before {
  border-bottom-color: #86efac;
}

.system-message p {
  margin: 0;
  font-size: 13px;
  color: #334155;
  line-height: 1.5;
}

.conversation-item {
  margin-bottom: 12px;
}

.message-row {
  display: flex;
  align-items: flex-start;
  animation: slideIn 0.25s ease;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.avatar-wrapper {
  flex-shrink: 0;
}

.avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

.ai-avatar {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
  margin-right: 10px;
}

.user-avatar {
  background: linear-gradient(135deg, #22c55e 0%, #15803d 100%);
  color: white;
  margin-left: -6px;
  z-index: 1;
}

.message-content-wrapper {
  flex: 1;
  display: flex;
}

.ai-row .message-content-wrapper {
  justify-content: flex-start;
}

.user-row {
  flex-direction: row-reverse;
}

.user-row .message-content-wrapper {
  justify-content: flex-end;
}

.message-bubble {
  max-width: 80%;
  padding: 10px 14px;
  border-radius: 14px;
  position: relative;
}

.ai-bubble {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 14px 14px 14px 5px;
}

.user-bubble {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  border-radius: 14px 14px 5px 14px;
}

.question-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}

.user-meta {
  justify-content: flex-end;
}

.question-badge {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 10px;
  font-weight: 500;
}

.message-time {
  font-size: 11px;
  color: #94a3b8;
  margin-left: 8px;
}

.user-bubble .message-time {
  color: rgba(255, 255, 255, 0.7);
  margin-left: 0;
}

.message-text {
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: #334155;
}

.user-bubble .message-text {
  color: white;
}

.input-area {
  padding: 10px 12px;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
}

.input-wrapper {
  background: white;
  border-radius: 8px;
  padding: 8px;
  border: 1px solid #e2e8f0;
  transition: all 0.25s ease;
}

.input-wrapper:focus-within {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.answer-input {
  width: 100%;
  height: 40px;
  padding: 8px 10px;
  border: none;
  border-radius: 6px;
  resize: none;
  font-size: 13px;
  box-sizing: border-box;
  background: transparent;
}

.answer-input:focus {
  outline: none;
}

.answer-input::placeholder {
  color: #94a3b8;
}

.answer-input:disabled {
  background: #f1f5f9;
  color: #94a3b8;
}

.input-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #e2e8f0;
}

.input-hints {
  display: flex;
  align-items: center;
  gap: 10px;
}

.char-count {
  font-size: 12px;
  color: #94a3b8;
}

.hint-text {
  font-size: 11px;
  color: #3b82f6;
}

.submit-btn {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  border: none;
  padding: 8px 22px;
  border-radius: 16px;
  color: white;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: all 0.25s ease;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 3px 10px rgba(59, 130, 246, 0.3);
}

.submit-btn:disabled {
  background: #cbd5e1;
  cursor: not-allowed;
}

.submit-btn.full-width {
  width: 100%;
  justify-content: center;
  padding: 12px;
  font-size: 14px;
}

.progress-container {
  max-width: 100%;
  margin: 10px auto;
}

.progress-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.progress-text {
  font-size: 13px;
  color: #64748b;
}

.progress-count {
  font-size: 13px;
  font-weight: 600;
  color: #3b82f6;
}

.progress-bar {
  height: 6px;
  background: #e2e8f0;
  border-radius: 3px;
  overflow: hidden;
  position: relative;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #3b82f6 0%, #60a5fa 100%);
  border-radius: 3px;
  transition: width 0.4s ease;
  position: relative;
}

.progress-fill::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 50%;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0.3), transparent);
}

.progress-glow {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 16px;
  height: 16px;
  background: rgba(59, 130, 246, 0.3);
  border-radius: 50%;
  transition: left 0.4s ease;
}

.result-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 10px;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.result-modal {
  background: white;
  border-radius: 12px;
  max-width: 480px;
  width: 100%;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
  animation: scaleIn 0.25s ease;
}

@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.result-header {
  padding: 20px;
  text-align: center;
  color: white;
  position: relative;
  overflow: hidden;
}

.result-header::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -50%;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
}

.result-header.不合格 {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
}

.result-header.合格 {
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
}

.result-header.优秀 {
  background: linear-gradient(135deg, #3b82f6 0%, #1e40af 100%);
}

.result-icon {
  font-size: 40px;
  margin-bottom: 8px;
}

.result-title {
  font-size: 19px;
  margin: 0 0 10px 0;
  font-weight: 600;
}

.score-display {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 3px;
}

.score {
  font-size: 48px;
  font-weight: 700;
  line-height: 1;
}

.score-unit {
  font-size: 16px;
  opacity: 0.9;
}

.result-body {
  padding: 18px;
}

.result-item {
  margin-bottom: 15px;
}

.grade-item {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  background: #f8fafc;
  border-radius: 8px;
}

.grade-item .label {
  font-size: 14px;
  color: #64748b;
}

.grade-item .value {
  font-size: 16px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 16px;
}

.grade.不合格 {
  background: #fee2e2;
  color: #dc2626;
}

.grade.合格 {
  background: #dcfce7;
  color: #16a34a;
}

.grade.优秀 {
  background: #dbeafe;
  color: #1e40af;
}

.result-section {
  margin-bottom: 18px;
}

.result-section:last-child {
  margin-bottom: 0;
}

.result-section h3 {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 15px;
  color: #334155;
  margin: 0 0 10px 0;
  font-weight: 600;
}

.result-section p {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: #64748b;
  text-align: justify;
}

.result-section ul {
  margin: 0;
  padding-left: 0;
  list-style: none;
}

.result-section li {
  position: relative;
  font-size: 13px;
  line-height: 1.5;
  color: #64748b;
  padding: 8px 10px 8px 35px;
  margin-bottom: 6px;
  background: #f8fafc;
  border-radius: 6px;
  border-left: 3px solid #3b82f6;
}

.result-section li:last-child {
  margin-bottom: 0;
}

.suggestion-number {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  width: 18px;
  height: 18px;
  background: #3b82f6;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
}

.result-footer {
  padding: 15px 18px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  gap: 10px;
}

.close-btn {
  flex: 1;
  padding: 10px;
  border-radius: 20px;
  font-size: 13px;
  border: 1px solid #e2e8f0;
  color: #64748b;
}

.close-btn:hover {
  background: #f8fafc;
}

.retest-btn {
  flex: 2;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  border: none;
  padding: 10px;
  border-radius: 20px;
  color: white;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
}

.retest-btn:hover {
  background: linear-gradient(135deg, #3b82f6 0%, #1e40af 100%);
}

@media (max-width: 768px) {
  .interview-container {
    margin-top: 60px;
    padding: 5px;
  }
  
  .page-title {
    font-size: 22px;
  }
  
  .chat-container {
    height: calc(100vh - 240px);
  }
  
  .message-bubble {
    max-width: 88%;
  }
  
  .avatar {
    width: 34px;
    height: 34px;
    font-size: 14px;
  }
  
  .result-modal {
    max-height: 90vh;
    border-radius: 10px;
  }
  
  .result-header {
    padding: 15px;
  }
  
  .result-body {
    padding: 15px;
  }
  
  .result-footer {
    flex-direction: column;
  }
  
  .close-btn, .retest-btn {
    width: 100%;
  }
}

.evaluate-section {
  display: flex;
  justify-content: center;
  padding: 20px;
}

.evaluate-btn {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border: none;
  padding: 14px 40px;
  border-radius: 30px;
  color: white;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s;
}

.evaluate-btn:hover {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4);
}

.evaluate-btn:disabled {
  background: #9ca3af;
  transform: none;
  box-shadow: none;
}

/* 答题详情样式 */
.details-list {
  margin-top: 10px;
}

.detail-item {
  background: #f8fafc;
  border-radius: 8px;
  padding: 14px;
  margin-bottom: 12px;
  border: 1px solid #e2e8f0;
}

.detail-item:last-child {
  margin-bottom: 0;
}

.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px dashed #e2e8f0;
}

.detail-number {
  font-size: 13px;
  font-weight: 600;
  color: #3b82f6;
}

.detail-score {
  font-size: 16px;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 16px;
}

.detail-score.score-high {
  background: #dcfce7;
  color: #16a34a;
}

.detail-score.score-medium {
  background: #fef3c7;
  color: #d97706;
}

.detail-score.score-low {
  background: #fecaca;
  color: #dc2626;
}

.detail-score.score-fail {
  background: #fee2e2;
  color: #b91c1c;
}

.detail-question,
.detail-answer {
  font-size: 13px;
  line-height: 1.5;
  margin-bottom: 8px;
  color: #334155;
}

.detail-question .label,
.detail-answer .label {
  color: #64748b;
  font-weight: 500;
}

.detail-strengths,
.detail-weaknesses {
  margin-bottom: 8px;
}

.detail-strengths .label,
.detail-weaknesses .label {
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 6px;
  display: block;
}

.strengths-label {
  color: #16a34a;
}

.weaknesses-label {
  color: #dc2626;
}

.tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.tag-list .tag {
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 4px;
}

.tag-list.strengths .tag {
  background: #dcfce7;
  color: #16a34a;
}

.tag-list.weaknesses .tag {
  background: #fee2e2;
  color: #dc2626;
}

.detail-improvement {
  padding: 10px;
  background: #dbeafe;
  border-radius: 6px;
  margin-top: 8px;
}

.improvement-label {
  font-size: 12px;
  font-weight: 600;
  color: #1e40af;
}

.improvement-text {
  font-size: 13px;
  color: #334155;
  line-height: 1.5;
}

/* AI生成内容免责声明样式 */
.disclaimer-section {
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px dashed #e5e7eb;
}

.disclaimer-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  background-color: #fef3c7;
  border: 1px solid #fbbf24;
  border-radius: 8px;
  font-size: 13px;
  color: #b45309;
}

.disclaimer-content i {
  font-size: 16px;
}
</style>



